/**
 * Centralized Just-In-Time onboarding & contextual guidance copy.
 *
 * RULE: Do not hardcode instructional strings in UI components.
 * Import from this module and render via ChatGuideCard / EmptyStateGuide.
 *
 * @see docs/ux_onboarding_guide_spec.md
 */

export const ONBOARDING_COPY_VERSION = '1.0.0';

/** Shared layout token for chat-injected guide cards */
export const GUIDE_CARD_TOKEN = 'GUIDE_CARD';

/** Visual variants for guide cards and empty states */
export const GUIDE_VARIANTS = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ACTION: 'action',
};

/**
 * @typedef {Object} OnboardingSnippet
 * @property {string} id - Stable key for i18n / telemetry
 * @property {string} title
 * @property {string} body
 * @property {string} [cta] - Primary button label
 * @property {string} [ctaRoute] - Named route (react-router route name)
 * @property {string} [secondaryCta]
 * @property {string} [secondaryCtaRoute]
 * @property {'info'|'success'|'warning'|'action'} [variant]
 * @property {Array<'customer'|'provider'>} [roles] - If set, snippet is role-specific
 */

/**
 * @typedef {Object} ChatGuideEntry
 * @property {string} token - DOM data-guide-token value
 * @property {string} id
 * @property {string} title
 * @property {string} body
 * @property {string} [cta]
 * @property {'info'|'success'|'warning'|'action'} variant
 * @property {Object} [byRole] - { customer?: partial, provider?: partial }
 */

/** Wallet connection — login / Web3 auth surfaces */
export const walletConnection = {
  id: 'onboarding.walletConnection',
  title: 'Your wallet, your keys',
  body:
    'Connecting a wallet proves you own an address — it is a cryptographic signature only, not a payment. BetterXchange never stores your seed phrase or moves funds on your behalf.',
  hint:
    'Settlement stays non-custodial. Signing in with a wallet does not lock or transfer assets.',
  cta: 'Connect wallet & sign in',
  variant: GUIDE_VARIANTS.INFO,
};

/** Escrow creation — DePay / secure trade upgrade */
export const escrowCreation = {
  id: 'onboarding.escrowCreation',
  title: 'Funds lock in the contract — not with the seller',
  body:
    'When you secure a trade, USDC is sent to an immutable smart contract on-chain. Neither BetterXchange nor the other party can spend it until the Handshake PIN oracle confirms delivery or a dispute is resolved.',
  reassurance:
    'You are always interacting with public contract code, not a private bank account controlled by a stranger.',
  cta: 'Secure Trade in Escrow',
  variant: GUIDE_VARIANTS.ACTION,
};

/** Handshake PIN — role-specific instructions */
export const handshakePin = {
  id: 'onboarding.handshakePin',
  buyer: {
    id: 'onboarding.handshakePin.buyer',
    title: 'Guard your PIN until you verify the item',
    body:
      'Your Handshake PIN is proof of physical receipt. Never share it in chat, over the phone, or before you inspect the item in person. Only hand it to the seller once you are satisfied with what you received.',
    variant: GUIDE_VARIANTS.WARNING,
  },
  seller: {
    id: 'onboarding.handshakePin.seller',
    title: 'The PIN unlocks your payout proof',
    body:
      'When the buyer gives you the PIN in person, enter it here to confirm handoff. The offline oracle records POST_PIN state — this protects your instant payout claim if a dispute arises.',
    cta: 'Verify PIN',
    variant: GUIDE_VARIANTS.SUCCESS,
  },
};

/**
 * Chat-injected guide cards keyed by transaction phase.
 * Resolved via getChatGuideForState().
 */
export const chatGuides = {
  inquiry: {
    token: 'GUIDE_INQUIRY',
    id: 'onboarding.chat.inquiry',
    title: 'Ask freely — no payment required',
    body:
      'This is a pre-commitment conversation. Use chat to negotiate terms, ask questions, or arrange a meetup. When both sides are ready, upgrade to Secure Trade in Escrow.',
    variant: GUIDE_VARIANTS.INFO,
  },
  escrowStaging: {
    token: 'GUIDE_ESCROW_STAGING',
    id: 'onboarding.chat.escrowStaging',
    title: 'Ready to lock funds on-chain?',
    body:
      'Upgrading starts the escrow flow. Your payment goes to a smart contract — not the seller\'s personal wallet — and the Handshake PIN tracks physical delivery.',
    cta: 'Continue to escrow',
    variant: GUIDE_VARIANTS.ACTION,
    byRole: {
      customer: {
        body:
          'You are about to fund escrow on-chain. Review the listing price and delivery plan before confirming. Funds stay in the contract until PIN verification.',
      },
      provider: {
        body:
          'The buyer is upgrading this chat to secured escrow. Once funded on-chain, prepare for in-person handoff and PIN verification.',
      },
    },
  },
  escrowSecured: {
    token: 'GUIDE_ESCROW_SECURED',
    id: 'onboarding.chat.escrowSecured',
    title: 'Trade secured on-chain',
    body:
      'Payment is confirmed in the smart contract. Coordinate meetup details here, then complete the Handshake PIN exchange in person.',
    variant: GUIDE_VARIANTS.SUCCESS,
  },
  handshakePrePin: {
    token: 'GUIDE_HANDSHAKE_PRE_PIN',
    id: 'onboarding.chat.handshakePrePin',
    title: 'Meet in person to complete the trade',
    body:
      'Escrow is active. Complete the physical exchange, then use the Handshake PIN step to confirm delivery.',
    variant: GUIDE_VARIANTS.WARNING,
    byRole: {
      customer: {
        title: 'Keep your PIN private until inspection',
        body: handshakePin.buyer.body,
      },
      provider: {
        title: 'Await in-person handoff',
        body:
          'Do not release the item until you are ready to verify the buyer\'s PIN. PIN entry confirms delivery for payout purposes.',
      },
    },
  },
  handshakePostPin: {
    token: 'GUIDE_HANDSHAKE_POST_PIN',
    id: 'onboarding.chat.handshakePostPin',
    title: 'Physical handoff confirmed',
    body:
      'The Handshake PIN was verified. The offline oracle recorded POST_PIN state. Escrow release or dispute rules now apply per contract terms.',
    variant: GUIDE_VARIANTS.SUCCESS,
  },
  tradeEnded: {
    token: 'GUIDE_TRADE_ENDED',
    id: 'onboarding.chat.tradeEnded',
    title: 'Trade complete',
    body:
      'This secured trade has ended. Leave a rating if prompted to help build network trust.',
    variant: GUIDE_VARIANTS.INFO,
  },
  /** Service/Proxy mobile runner — route execution */
  proxyRunnerRoute: {
    token: 'GUIDE_PROXY_RUNNER_ROUTE',
    id: 'onboarding.chat.proxyRunnerRoute',
    title: 'Navigate to pickup',
    body:
      'Open maps and head to the store location. Tap notify when you arrive so the customer can track progress.',
    variant: GUIDE_VARIANTS.ACTION,
    byRole: {
      provider: {
        title: 'Runner: open route to store',
        body:
          'Use the map link for turn-by-turn navigation. High-glare mode keeps the checklist readable outdoors.',
      },
    },
  },
  proxyRunnerArrived: {
    token: 'GUIDE_PROXY_RUNNER_ARRIVED',
    id: 'onboarding.chat.proxyRunnerArrived',
    title: 'Customer notified — you have arrived',
    body:
      'The buyer sees your arrival alert. Confirm escrow is funded before collecting items and completing handoff.',
    variant: GUIDE_VARIANTS.SUCCESS,
  },
  proxyRunnerPin: {
    token: 'GUIDE_PROXY_RUNNER_PIN',
    id: 'onboarding.chat.proxyRunnerPin',
    title: 'Enter Handshake PIN at handoff',
    body: handshakePin.seller.body,
    variant: GUIDE_VARIANTS.SUCCESS,
    cta: 'Verify PIN',
  },
  /** Service/Proxy mobile customer — tracker lane */
  proxyCustomerTrack: {
    token: 'GUIDE_PROXY_CUSTOMER_TRACK',
    id: 'onboarding.chat.proxyCustomerTrack',
    title: 'Your runner is on the way',
    body:
      'Track live status updates below. Keep your Handshake PIN private until you verify the items in person.',
    variant: GUIDE_VARIANTS.INFO,
    byRole: {
      customer: {
        title: 'Track your proxy order',
        body: handshakePin.buyer.body,
      },
    },
  },
  proxyCustomerPinReveal: {
    token: 'GUIDE_PROXY_CUSTOMER_PIN',
    id: 'onboarding.chat.proxyCustomerPin',
    title: 'Your secure Handshake PIN',
    body: handshakePin.buyer.body,
    variant: GUIDE_VARIANTS.WARNING,
  },
  proxyRatingPrompt: {
    token: 'GUIDE_PROXY_RATING',
    id: 'onboarding.chat.proxyRating',
    title: 'Handoff confirmed — rate your experience',
    body:
      'PIN verified and escrow rules applied. A quick rating helps the network identify trusted runners and buyers.',
    variant: GUIDE_VARIANTS.ACTION,
    cta: 'Leave a review',
  },
};

/** Interactive empty states — high-conversion CTAs when data is null */
export const emptyStates = {
  assetMarket: {
    id: 'onboarding.empty.assetMarket',
    title: 'No listings yet',
    body: 'Be the first to post an asset on the network. Listings appear here as soon as they are published.',
    cta: 'Create listing',
    ctaRoute: 'NewListingPage',
    variant: GUIDE_VARIANTS.ACTION,
  },
  inbox: {
    id: 'onboarding.empty.inbox',
    title: 'No conversations yet',
    body: 'Browse the Asset Market and tap Inquire / Message Seller to start a free pre-commitment chat.',
    cta: 'Browse listings',
    ctaRoute: 'SearchPage',
    secondaryCta: 'View landing page',
    secondaryCtaRoute: 'LandingPage',
    variant: GUIDE_VARIANTS.INFO,
  },
  manageListings: {
    id: 'onboarding.empty.manageListings',
    title: 'You have no listings',
    body: 'Publish your first asset to appear in Town Hall\'s Asset Market and start receiving buyer inquiries.',
    cta: 'Create listing',
    ctaRoute: 'NewListingPage',
    variant: GUIDE_VARIANTS.ACTION,
  },
  verification: {
    id: 'onboarding.empty.verification',
    title: 'Complete verification to unlock full trust',
    body:
      'Verified profiles help high-value buyers and sellers find you. Complete verification when you are ready to transact at scale.',
    cta: 'Go to profile',
    ctaRoute: 'ProfileSettingsPage',
    variant: GUIDE_VARIANTS.INFO,
  },
  townHall: {
    id: 'onboarding.empty.townHall',
    title: 'Town Hall is quiet',
    body: 'Share market observations, liquidity requests, or trade ideas — settlement always stays non-custodial on-chain.',
    cta: 'Post an update',
    variant: GUIDE_VARIANTS.INFO,
  },
};

/**
 * Maps Sharetribe / sovereign trade states to chatGuides keys.
 * @param {Object} params
 * @param {boolean} [params.isInquiryProcess]
 * @param {string} [params.processState]
 * @param {string} [params.tradeBannerStatus] - PENDING | SECURED | POST_PIN | ENDED
 * @param {boolean} [params.isEscrowStaging]
 * @param {boolean} [params.isOnchainSecured]
 * @returns {string|null} chatGuides key
 */
export const resolveChatGuideStateKey = ({
  isInquiryProcess,
  processState,
  tradeBannerStatus,
  isEscrowStaging,
  isOnchainSecured,
}) => {
  if (tradeBannerStatus === 'ENDED') {
    return 'tradeEnded';
  }
  if (tradeBannerStatus === 'POST_PIN') {
    return 'handshakePostPin';
  }
  if (tradeBannerStatus === 'SECURED') {
    return 'handshakePrePin';
  }
  if (isOnchainSecured && !tradeBannerStatus) {
    return 'handshakePrePin';
  }
  if (tradeBannerStatus === 'PENDING' || isEscrowStaging) {
    return 'escrowStaging';
  }
  if (isInquiryProcess && processState === 'free-inquiry') {
    return 'inquiry';
  }
  return null;
};

/**
 * Returns merged chat guide copy for a transaction phase and optional role.
 * @param {string} stateKey - chatGuides key
 * @param {Object} [options]
 * @param {'customer'|'provider'} [options.role]
 * @returns {ChatGuideEntry|null}
 */
export const getChatGuideForState = (stateKey, options = {}) => {
  const base = chatGuides[stateKey];
  if (!base) {
    return null;
  }

  const { role } = options;
  const roleOverlay =
    role && base.byRole?.[role === 'customer' ? 'customer' : 'provider'];

  return {
    ...base,
    ...(roleOverlay || {}),
    layoutToken: GUIDE_CARD_TOKEN,
  };
};

/**
 * Lookup empty state config by surface key.
 * @param {'assetMarket'|'inbox'|'manageListings'|'verification'|'townHall'} surfaceKey
 * @returns {OnboardingSnippet}
 */
export const getEmptyState = surfaceKey => emptyStates[surfaceKey] || null;

/** Aggregated export for convenient default import */
export const onboardingCopy = {
  version: ONBOARDING_COPY_VERSION,
  walletConnection,
  escrowCreation,
  handshakePin,
  chatGuides,
  emptyStates,
};

export default onboardingCopy;
